import { APIClient, ApiQueryParams } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import {
    CreateGroup,
    CreateGroupMember,
    GroupListResponse,
    GroupMember,
    GroupMemberListResponse,
    GroupMemberQueryParams,
    GroupQueryParams,
    IGroup,
    UpdateGroup,
    UpdateGroupMember
} from '@frontend/group/types';

export class EquipmentGroupClient extends APIClient {
    public static readonly endpoint = '/group-api/v1';

    public static async fetchEquipmentGroups(queryParams?: ApiQueryParams<GroupQueryParams>): Promise<GroupListResponse> {
        return await this.apiPaginated<GroupListResponse, GroupQueryParams>(`${this.endpoint}/equipment-groups`, queryParams, undefined);
    }

    public static async resolveEquipmentGroups(ids: string[], field: string = 'id'): Promise<GroupListResponse> {
        return this.resolve<GroupListResponse>(`${this.endpoint}/equipment-groups`, ids, field);
    }

    public static async deleteEquipmentGroups(groupIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/equipment-groups-delete`, { ids: groupIds });
        return await this.handleVoidResponse(response);
    }

    public static async postEquipmentGroup(accountId: string, group: CreateGroup): Promise<IGroup> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/equipment-groups`, group);
        return await this.handleResponse<IGroup>(response);
    }

    public static async fetchEquipmentGroup(accountId: string, groupId: string): Promise<IGroup> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/equipment-groups/${groupId}`);
        return await this.handleResponse<IGroup>(response);
    }

    public static async patchEquipmentGroup(accountId: string, groupId: string, group: UpdateGroup): Promise<IGroup> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/equipment-groups/${groupId}`, group);
        return await this.handleResponse<IGroup>(response);
    }

    public static async deleteEquipmentGroup(accountId: string, groupId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/equipment-groups/${groupId}`);
        return await this.handleVoidResponse(response);
    }

    public static async fetchGroupsNotInGroup(accountId: string, groupId: string): Promise<GroupListResponse> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/equipment-groups/${groupId}/groups`);
        return await this.handleResponse<GroupListResponse>(response);
    }

    //IGroup members
    public static async fetchEquipmentGroupMembers(queryParams?: ApiQueryParams<GroupMemberQueryParams>): Promise<GroupMemberListResponse> {
        return await this.apiPaginated<GroupMemberListResponse, GroupMemberQueryParams>(`${this.endpoint}/equipment-group-members`, queryParams);
    }

    public static async resolveEquipmentGroupMembers(ids: string[], field: string = 'id'): Promise<GroupMemberListResponse> {
        return this.resolve<GroupMemberListResponse>(`${this.endpoint}/equipment-group-members`, ids, field);
    }

    public static async deleteEquipmentGroupMembers(memberIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/equipment-group-members-delete`, { ids: memberIds });
        return await this.handleVoidResponse(response);
    }

    public static async postEquipmentGroupMember(accountId: string, groupId: string, groupMember: CreateGroupMember): Promise<GroupMember> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/equipment-groups/${groupId}/members`, groupMember);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async postEquipmentGroupMemberList(accountId: string, groupId: string, entity_type: EntityType, memberIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/equipment-groups/${groupId}/${entity_type}s/members-list`, { ids: memberIds });
        return await this.handleVoidResponse(response);
    }

    public static async fetchEquipmentGroupMember(accountId: string, groupId: string, memberId: string): Promise<GroupMember> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/equipment-groups/${groupId}/members/${memberId}`);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async patchEquipmentGroupMember(accountId: string, groupId: string, memberId: string, body: UpdateGroupMember): Promise<GroupMember> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/equipment-groups/${groupId}/members/${memberId}`, body);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async deleteEquipmentGroupMember(accountId: string, groupId: string, memberId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/equipment-groups/${groupId}/members/${memberId}`);
        return await this.handleVoidResponse(response);
    }
}
